// Tipos y piezas comunes de «Envíos de proveedor» y «Recepción de rosas» (backend: /api/recepcion-rosas)
import { useAuth } from '@/contexts/AuthContext'
import { CheckCircle2, ClipboardCheck, ExternalLink, FileCheck2, PackageOpen, ShieldX, Truck, type LucideIcon } from 'lucide-react'

export type EstadoEnvio = 'facturado' | 'llegado' | 'rosas_verificados' | 'revisado' | 'entrada_factusol'
export type Resultado = 'ok' | 'falta' | 'danado'

export interface Envio {
  id: number
  proveedor: string
  documento: string
  supplier_code: string
  supplier_name: string
  doc_date: string | null
  factusol_order: number | null
  factusol_ref: string | null
  web_order: string | null
  total: number | null
  n_lines: number
  units: number
  facturado_at: string
  llegado_at: string | null
  llegado_por_nombre: string | null
  rosas_at: string | null
  rosas_por_nombre: string | null
  revisado_at: string | null
  revisado_por_nombre: string | null
  entrada_at: string | null
  entrada_origen: string | null
  entrada_ref: string | null
  entrada_por_nombre: string | null
  estado: EstadoEnvio
  estado_texto: string
  // pendientes = líneas rosas sin marcar · clientes_pendientes = pedidos distintos con líneas sin marcar
  rosas: { total: number; verificadas: number; ok: number; incidencias: number; pedidos: number; pendientes?: number; clientes_pendientes?: number }
  // días desde que el proveedor lo envió (hasta la llegada u hoy) frente al plazo de ese proveedor (días laborables)
  plazo?: { dias: number; laborables: number; plazo: number; fuera: boolean }
  alertas: { tipo: 'llegado_sin_verificar' | 'no_llega'; texto: string }[]
  url_hoja: string | null
}

export interface LineaRosa {
  linea: string
  clave: string
  referencia: string
  ref_proveedor: string
  descripcion: string
  unidades: number
  pedido: string
  id_order: number | null
  manual_id: number | null
  cliente?: string | null
  origen_pedido?: OrigenPedido | null  // null = pieza añadida a mano sin nº de pedido («a mano #N»)
  dias: number | null
  fecha_pedido: string | null
  resultado: Resultado | null
  nota: string | null
  usuario: string | null
  usuario_nombre: string | null
  at: string | null
}

export interface EnvioDetalle extends Envio {
  lineas_rosas: LineaRosa[]
  lineas: [string, string, string, number][]
  eventos: { tipo: string; detalle: string | null; usuario: string | null; usuario_nombre: string | null; at: string }[]
  pendiente_aviso: number
  usuario: string
  es_admin: boolean
}

export const PERMISOS_RECEPCION = ['logistics.stock_check', 'reports.pending_parts', 'reports.supplier_orders']

export function useAccesoRecepcion() {
  const { hasPermission } = useAuth()
  return PERMISOS_RECEPCION.some((p) => hasPermission(p))
}

export function SinPermiso() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
      <ShieldX className="w-16 h-16 text-red-500 mb-4" />
      <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Acceso denegado</h1>
      <p className="text-slate-600 dark:text-slate-400 max-w-md">
        Hace falta el permiso de almacén («Comprobar stock»), «Piezas pendientes» o «Pedidos a proveedores».
      </p>
    </div>
  )
}

// "2026-10-19T10:05:00" -> "19/10 10:05"
export const fmtFecha = (s?: string | null) =>
  !s ? '—' : `${s.slice(8, 10)}/${s.slice(5, 7)}${s.length > 10 && s.slice(11, 16) !== '00:00' ? ' ' + s.slice(11, 16) : ''}`

export const nombreCorto = (n?: string | null) => (n || '').split(' ')[0]

export const ESTADO_ESTILO: Record<EstadoEnvio, string> = {
  facturado: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300',
  llegado: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  rosas_verificados: 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
  revisado: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  entrada_factusol: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
}

const PASOS: { estado: EstadoEnvio; texto: string; icon: LucideIcon; at: keyof Envio; por: keyof Envio }[] = [
  { estado: 'facturado', texto: 'Facturado / enviado', icon: Truck, at: 'facturado_at', por: 'supplier_name' },
  { estado: 'llegado', texto: 'Llegado al almacén', icon: PackageOpen, at: 'llegado_at', por: 'llegado_por_nombre' },
  { estado: 'rosas_verificados', texto: 'Rosas verificados', icon: CheckCircle2, at: 'rosas_at', por: 'rosas_por_nombre' },
  { estado: 'revisado', texto: 'Revisado completo', icon: ClipboardCheck, at: 'revisado_at', por: 'revisado_por_nombre' },
  { estado: 'entrada_factusol', texto: 'Entrada en Factusol', icon: FileCheck2, at: 'entrada_at', por: 'entrada_por_nombre' },
]

// Recorrido del envío: 5 puntos (hecho / siguiente / saltado / pendiente) con fecha y quién al pasar el ratón. Un paso sin
// marcar con otro posterior ya hecho (p. ej. entrada en Factusol sin «revisado completo») se ha saltado: no es el siguiente.
export function Recorrido({ envio, conTexto = false }: { envio: Envio; conTexto?: boolean }) {
  const ultimo = PASOS.reduce((u, p, i) => (envio[p.at] ? i : u), 0)
  const siguiente = ultimo + 1 < PASOS.length ? ultimo + 1 : -1
  const actual = ultimo
  const textoPaso = (i: number) => {
    const p = PASOS[i]
    const at = envio[p.at] as string | null
    const por = p.estado === 'entrada_factusol' && envio.entrada_origen === 'factusol' ? 'Factusol' : (envio[p.por] as string | null)
    return `${p.texto}${at ? ` · ${fmtFecha(at)}${por ? ' · ' + (p.estado === 'facturado' ? por : nombreCorto(por)) : ''}`
      : i === siguiente ? ' · siguiente paso' : i < ultimo ? ' · sin marcar' : ' · pendiente'}`
  }
  const resumen = PASOS.map((_, i) => textoPaso(i)).join('\n')
  return (
    <span className="inline-flex items-center gap-2 shrink-0" title={resumen} aria-label={`Recorrido: ${resumen.replace(/\n/g, '; ')}`} role="img">
      <span className="inline-flex items-center">
        {PASOS.map((p, i) => {
          const hecho = !!envio[p.at]
          return (
            <span key={p.estado} className="inline-flex items-center">
              {i > 0 && <span className={`h-0.5 w-2.5 ${i <= ultimo ? 'bg-emerald-500' : 'bg-slate-200 dark:bg-slate-700'}`} />}
              <span
                title={textoPaso(i)}
                data-paso={hecho ? 'hecho' : i === siguiente ? 'siguiente' : i < ultimo ? 'saltado' : 'pendiente'}
                className={`block w-2.5 h-2.5 rounded-full ${
                  hecho ? 'bg-emerald-500' : i === siguiente ? 'bg-white ring-2 ring-pink-500 dark:bg-slate-900'
                    : i < ultimo ? 'bg-white ring-2 ring-slate-300 dark:bg-slate-900 dark:ring-slate-600' : 'bg-slate-200 dark:bg-slate-700'
                }`}
              />
            </span>
          )
        })}
      </span>
      {conTexto && <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">{PASOS[actual].texto}</span>}
    </span>
  )
}

export const RESULTADO_ESTILO: Record<Resultado, { texto: string; largo?: string; cls: string; tecla: string }> = {
  ok: { texto: 'OK', cls: 'bg-emerald-600 text-white', tecla: '1' },
  falta: { texto: 'Falta', cls: 'bg-red-600 text-white', tecla: '2' },
  danado: { texto: 'Dañado', largo: 'Dañado / incorrecto', cls: 'bg-orange-500 text-white', tecla: '3' },
}

// ------------------------------------------------------------------ Nº de pedido: PrestaShop o Factusol
// PrestaShop y Factusol numeran por separado: el nº de pedido siempre va con su origen («PS 135926» enlaza a la ficha del
// pedido en PrestaShop; «Factusol 22610» es un pedido directo, sin enlace). Mismo aspecto en Piezas pendientes y recepción.
export type OrigenPedido = 'prestashop' | 'factusol'
export const PS_ORDER_URL = 'https://www.jvs-informatica.com/admin058mixixqnew/index.php/sell/orders/{id}/view'

const ORIGEN_ESTILO: Record<OrigenPedido, { corto: string; cls: string }> = {
  prestashop: { corto: 'PS', cls: 'bg-pink-600 text-white dark:bg-pink-500' },
  factusol: { corto: 'Factusol', cls: 'bg-teal-600 text-white dark:bg-teal-500' },
}

export function EtiquetaPedido({ numero, origen, size = 'md', title, sinComprobar = false }: {
  numero: number | string
  origen: OrigenPedido
  size?: 'sm' | 'md' | 'lg'
  title?: string
  sinComprobar?: boolean
}) {
  const st = ORIGEN_ESTILO[origen]
  const num = size === 'lg' ? 'text-xl' : size === 'md' ? 'text-base' : 'text-sm'
  const contenido = (
    <>
      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide leading-none ${st.cls}`}>{st.corto}</span>
      <span className={`${num} font-bold tabular-nums text-slate-900 dark:text-white`}>{numero}</span>
      {origen === 'prestashop' && <ExternalLink className="w-3.5 h-3.5 text-slate-400 group-hover/ped:text-pink-600" />}
      {sinComprobar && (
        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">sin comprobar</span>
      )}
    </>
  )
  const cls = 'group/ped inline-flex items-center gap-1.5 whitespace-nowrap'
  if (origen === 'prestashop') {
    return (
      <a href={PS_ORDER_URL.replace('{id}', String(numero))} target="_blank" rel="noreferrer" data-pedido={`ps:${numero}`}
        className={`${cls} hover:underline decoration-pink-400 underline-offset-4`} title={title || `Abrir el pedido ${numero} en PrestaShop`}>
        {contenido}
      </a>
    )
  }
  return <span className={cls} data-pedido={`factusol:${numero}`} title={title || `Pedido directo ${numero} de Factusol`}>{contenido}</span>
}
